import React from 'react';
import { toast, ToastOptions, TypeOptions } from 'react-toastify';
import { css } from '@linaria/core';
import { Status } from 'api/grpc/resources';
import { ToastInfoIcon, ToastErrorIcon, ToastSuccessIcon, ToastWarningIcon } from './ToastMessageIcons';
const defaultToastStyles = "omponents__ToastMessage__ToastMessage_tsx-defaultToastStyles-dgs5syf";
export interface CTOptions {
  position?: 'top-left' | 'top-center' | 'top-right' | 'bottom-left' | 'bottom-center' | 'bottom-right';
  toastContainerID?: string;
  bar?: {
    size?: string;
  };
  renderIcon?: () => React.ReactNode;
  hideAfter?: number;
  heading?: React.ReactNode | string;
  className?: string;
  type?: TypeOptions;
}
const toastIconMap = {
  success: ToastSuccessIcon,
  error: ToastErrorIcon,
  info: ToastInfoIcon,
  warning: ToastWarningIcon
};
const DEFAULT_AUTO_CLOSE_MS = 5000;
const DEFAULT_THROTTLE_MS = 3000; // 3 seconds throttle by default

// Store for throttling errors
const errorThrottleMap = new Map<string, number>();
const mapOptions = (cogoOptions: CTOptions = {}): ToastOptions => {
  const reactToastifyOptions: ToastOptions = {
    position: cogoOptions.position || 'top-center',
    autoClose: cogoOptions.hideAfter !== undefined ? cogoOptions.hideAfter * 1000 : DEFAULT_AUTO_CLOSE_MS,
    hideProgressBar: true,
    pauseOnHover: true,
    closeOnClick: false,
    className: defaultToastStyles
  };
  const DefaultIcon = toastIconMap[(cogoOptions.type as keyof typeof toastIconMap) || 'info'];
  if (cogoOptions.renderIcon) {
    reactToastifyOptions.icon = cogoOptions.renderIcon();
  } else {
    reactToastifyOptions.icon = <div className="toast-alert-icon">
        <DefaultIcon size={36} />
      </div>;
  }
  return reactToastifyOptions;
};
const makeToastContent = (message: React.ReactNode, options?: CTOptions) => {
  return <div className="toast-content">
      {options?.heading && <div className="toast-heading">{options.heading}</div>}
      <div className="toast-message">{message}</div>
    </div>;
};

// Helper function to generate throttle key from Status data
const generateThrottleKey = (data: Status.AsObject): string => {
  return `${data.code}_${data.description}_${data.details}`;
};

// Helper function to check if error should be throttled
const shouldThrottleError = (data: Status.AsObject, throttleMs: number = DEFAULT_THROTTLE_MS): boolean => {
  const key = generateThrottleKey(data);
  const now = Date.now();
  const lastShown = errorThrottleMap.get(key);
  if (!lastShown || now - lastShown > throttleMs) {
    errorThrottleMap.set(key, now);
    return false;
  }
  return true;
};
const cogoToast = {
  success: (message: React.ReactNode | string | undefined, options?: CTOptions): void => {
    if (message === undefined) {
      console.warn('toastMessage.success called with undefined message');
      return;
    }
    const toastOpts = mapOptions({
      type: 'success',
      ...options
    });
    const toastContent = makeToastContent(message, options);
    toast.success(toastContent, toastOpts);
  },
  error: (message: React.ReactNode | string | undefined, options?: CTOptions & {
    data?: Status.AsObject;
    enableThrottle?: boolean;
    throttleMs?: number;
  }): void => {
    if (message === undefined) {
      console.warn('toastMessage.error called with undefined message');
      return;
    }

    // Check if error should be throttled
    if (options?.data && options?.enableThrottle) {
      const throttleMs = options.throttleMs ?? DEFAULT_THROTTLE_MS;
      if (shouldThrottleError(options.data, throttleMs)) {
        console.error('Error toast throttled:', options.data);
        return;
      }
    }
    const toastOpts = mapOptions({
      type: 'error',
      ...options
    });
    const toastContent = makeToastContent(message, options);
    toast.error(toastContent, toastOpts);
  },
  info: (message: React.ReactNode | string | undefined, options?: CTOptions): void => {
    if (message === undefined) {
      console.warn('toastMessage.info called with undefined message');
      return;
    }
    const toastOpts = mapOptions({
      type: 'info',
      ...options
    });
    const toastContent = makeToastContent(message, options);
    toast.info(toastContent, toastOpts);
  },
  warn: (message: React.ReactNode | string | undefined, options?: CTOptions): void => {
    if (message === undefined) {
      console.warn('toastMessage.info called with undefined message');
      return;
    }
    const toastOpts = mapOptions({
      type: 'warning',
      ...options
    });
    const toastContent = makeToastContent(message, options);
    toast.warn(toastContent, toastOpts);
  }
};
export default cogoToast;

require("./ToastMessage.linaria.module.css!=!../../node_modules/@linaria/webpack-loader/node_modules/@linaria/webpack5-loader/lib/outputCssLoader.js!./ToastMessage.tsx");